import { datadogLogs } from "@datadog/browser-logs";
import { REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY } from "@redotech/redo-model/storefront/cart-attributes";
import { AttachmentStrategy } from "@redotech/redo-model/team";
import {
  defaultTermsAndConditionsBlockedMessage,
  WidgetAutocheck,
} from "@redotech/redo-model/widget";
import {
  ExtensionShopifyClient,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { STANDARD_CHECKOUT_BUTTON } from "../../selectors";
import { safeCartAdd, safeUpdateCartAttribute } from "../cart";
import { shouldBlockCheckout } from "../terms-and-conditions";

export function getCheckoutOnClickEvent(
  addCoverage: boolean,
  {
    autocheck,
    client,
    itemsToAdd,
    cartParams,
    coverageCheckoutButtonClicked,
    setCoverageCheckoutButtonClicked,
  }: {
    autocheck: WidgetAutocheck;
    client: ExtensionShopifyClient;
    itemsToAdd: { items: Partial<ShopifyCartItem>[] };
    cartParams: any;
    coverageCheckoutButtonClicked: boolean;
    setCoverageCheckoutButtonClicked: React.Dispatch<
      React.SetStateAction<boolean>
    >;
  },
) {
  return async (event: React.MouseEvent | React.TouchEvent) => {
    // prevent default logic if we're inside a form
    event.preventDefault();

    // handle Terms and condition
    if (
      autocheck.termsAndConditions?.blockCheckoutIfNotAccepted &&
      autocheck.termsAndConditions.termsCheckboxSelector
    ) {
      if (
        shouldBlockCheckout(autocheck.termsAndConditions.termsCheckboxSelector)
      ) {
        alert(
          autocheck.termsAndConditions.blockedMessageText ||
            defaultTermsAndConditionsBlockedMessage,
        );
        return;
      }
    }

    // set opted in cart attribute
    await safeUpdateCartAttribute({
      client,
      attributes: {
        attributes: [
          {
            key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
            value: addCoverage ? "true" : "false",
          },
        ],
      },
      retries: 1,
    });

    const shouldAddProduct =
      autocheck?.attachmentStrategy ===
        AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT &&
      addCoverage &&
      autocheck.addProductOnOptInClick !== false;

    // if the coverage button was already clicked don't add the redo product again.
    if (shouldAddProduct && !coverageCheckoutButtonClicked) {
      // this blocks ensureProduct from removing the redo variant we're adding
      setCoverageCheckoutButtonClicked(true);
      await safeCartAdd({
        client,
        items: itemsToAdd,
        params: cartParams,
        retries: 2,
      });
    }

    // if someone clicks the non-coverage button and somehow had also clicked
    // the coverage button. set coverage button clicked to false so the redo product
    // will be removed
    if (!addCoverage) {
      setCoverageCheckoutButtonClicked(false);
    }

    // click merchants standard checkout button
    if (autocheck.clickStandardCheckoutButton?.enabled) {
      const selector =
        autocheck.clickStandardCheckoutButton.checkoutButtonSelector ||
        STANDARD_CHECKOUT_BUTTON;
      const checkoutButton = document.querySelector(selector) as
        | HTMLButtonElement
        | undefined;
      if (checkoutButton?.click) {
        checkoutButton.click();
        // wait 6.5 seconds. if we're not in the checkout send them there ourselves
        await new Promise((resolve) => setTimeout(resolve, 6500));
        datadogLogs.logger.info(
          "merchant's checkout button is too slow or not working. sending shopper to checkout",
        );
        window.location.href = window.location.origin + "/checkout";
        return;
      }
    }

    // send shopper directly to checkout
    window.location.href = window.location.origin + "/checkout";
  };
}
